import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Edit as EditIcon,
    LockReset,
} from '@mui/icons-material';
import api from '../../api/api';
import CustomTable from '../../ReusableComponents/Table/CustomTable';
import CustomSnackbar from '../../components/CustomSnackbar';
import SearchBar from '../../ReusableComponents/Search/SearchBar';
import { ButtonStyles } from '../../Styles/ButtonStyles';
import en_ja from '../../translations/en_ja';
import CreateNewAdmin from './CreateNewAdmin';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";

const AdminManagement = () => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [superAdminData, setSuperAdminData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [editMode, setEditMode] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarType, setSnackbarType] = useState('success')

    const [selectedRow, setSelectedRow] = useState(null);

    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [passwordData, setPasswordData] = useState({
        newPassword: '',
        confirmPassword: ''
    });

    const [passwordErrors, setPasswordErrors] = useState({});
    const [selectedAdminId, setSelectedAdminId] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const validatePasswordForm = () => {
        const errors = {};
        const { newPassword = '', confirmPassword = '' } = passwordData;

        if (!newPassword.trim()) {
            errors.newPassword = en_ja["Password is required"];
        } else if (newPassword.length < 6) {
            errors.newPassword = en_ja["Minimum 6 characters required"];
        }

        if (!confirmPassword.trim()) {
            errors.confirmPassword = en_ja["Confirm password is required"];
        }

        if (
            newPassword.trim() &&
            confirmPassword.trim() &&
            newPassword !== confirmPassword
        ) {
            errors.confirmPassword = en_ja["Passwords do not match"];
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleOpenPasswordDialog = (row) => {
        setSelectedAdminId(row.id);
        setPasswordData({ newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
        setShowPassword(false);
        setPasswordDialogOpen(true);
        setShowConfirmPassword(false);
    };

    const handleSetPassword = async () => {

        if (!validatePasswordForm()) return;

        try {
            const res = await api.post(
                `/v1/super-admin/set/admin-password/${selectedAdminId}`,
                {
                    newPassword: passwordData.newPassword,
                    confirmPassword: passwordData.confirmPassword
                }
            );

            if (res.status === 200) {
                setSnackbarMessage(en_ja["Password updated successfully"]);
                setSnackbarType("success");
                setSnackbarOpen(true);
                setPasswordDialogOpen(false);
                setPasswordData({ newPassword: '', confirmPassword: '' });
                setPasswordErrors({});
            }

        } catch (err) {
            setSnackbarMessage(
                err?.response?.data?.message || en_ja["Failed to update password"]
            );
            setSnackbarType("error");
            setSnackbarOpen(true);
        }
    };

    const handleEdit = (row) => {
        setSelectedRow(row);
        setEditMode(true);
        setDialogOpen(true);
    };

    const handleDialogSubmit = () => {
        getSuperAdmin();
    }

    const renderValue = (value) =>
        value && value.toString().trim() !== "" ? value : "-";

    const columns = [
        {
            id: "first_name",
            label: en_ja["First Name"],
            sortable: true,
            render: (row) => renderValue(row.first_name),
        },
        {
            id: "last_name",
            label: en_ja["Last Name"],
            sortable: true,
            render: (row) => renderValue(row.last_name),
        },
        {
            id: "name",
            label: en_ja["User Name"],
            sortable: true,
            render: (row) => renderValue(row.name),
        },
        {
            id: "email",
            label: en_ja["Email"],
            sortable: true,
            render: (row) => renderValue(row.email),
        },
        {
            id: "actions",
            label: en_ja["Actions"],
            sortable: false,
            render: (row) => (
                <Box display="flex" justifyContent="start" gap={1}>

                    {/* Edit */}
                    <Tooltip title={en_ja["Edit Admin"]} arrow>
                        <IconButton
                            aria-label="edit"
                            size="small"
                            onClick={() => handleEdit(row)}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip
                        title={
                            row.isActive
                                ? en_ja["Disable Admin"]
                                : en_ja["Enable Admin"]
                        }
                        arrow
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleToggleStatus(row)}
                        >
                            {row.isActive ? (
                                <Visibility fontSize="small" sx={{ color: "#dc3545" }} />
                            ) : (
                                <VisibilityOff fontSize="small" sx={{ color: "rgb(0 0 0 / 54%)" }} />
                            )}
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={en_ja["Change Password"]} arrow>
                        <IconButton
                            size="small"
                            onClick={() => handleOpenPasswordDialog(row)}
                        >
                            <LockReset fontSize="small" />
                        </IconButton>
                    </Tooltip>

                </Box>
            ),
        },

    ];

    useEffect(() => {
        getSuperAdmin();
    }, [])

    const getSuperAdmin = async () => {
        setLoading(true);
        try {
            const res = await api.get("/v1/super-admin/admin-users");
            if (res) {
                setSuperAdminData(res.data.data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;

        setPasswordData((prev) => ({
            ...prev,
            [name]: value
        }));

        setPasswordErrors((prev) => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleDialog = () => {
        setDialogOpen(false);
        setEditMode(false);
        setSelectedRow(null);
    };

    const handleAddAdmin = () => {
        setEditMode(false);
        setDialogOpen(true);
    }

    const handleSnackbarClose = () => {
        setSnackbarOpen(false)
        setSnackbarMessage('')
        setSnackbarType('success')
    }

    const handleToggleStatus = async (row) => {
        if (!row?.id) return;

        try {
            const payload = {
                isActive: !row.isActive,
            };

            const res = await api.put(`/v1/admin/${row.id}/status`, payload);

            if (res.status === 200) {
                setSnackbarMessage(
                    row.isActive
                        ? en_ja["Admin disabled successfully"]
                        : en_ja["Admin enabled successfully"]
                );
                setSnackbarType("success");
                setSnackbarOpen(true);

                getSuperAdmin();
            }
        } catch (err) {
            console.error(err);
            setSnackbarMessage(
                err?.response?.data?.message || en_ja["Failed to update status"]
            );
            setSnackbarType("error");
            setSnackbarOpen(true);
        }
    };

    return (
        <Box sx={{ mt: 2 }}>

            <CustomSnackbar
                isOpen={snackbarOpen}
                type={snackbarType}
                message={snackbarMessage}
                onClose={handleSnackbarClose}
            />

            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                my: 3
            }}>
                <Box>
                    <SearchBar
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Box>
                <Button sx={ButtonStyles} onClick={handleAddAdmin}>
                    {en_ja["Create New Admin"]}
                </Button>
            </Box>

            <Card
                sx={{
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    border: '1px solid rgba(0, 0, 0, 0.05)',
                    overflow: 'hidden'
                }}
            >
                <CustomTable
                    columns={columns}
                    rows={superAdminData}
                    loading={loading}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    showIndexColumn={true}
                />
            </Card>

            <CreateNewAdmin
                open={dialogOpen}
                onClose={handleDialog}
                editMode={editMode}
                onSubmit={handleDialogSubmit}
                setSnackbarType={setSnackbarType}
                setSnackbarOpen={setSnackbarOpen}
                setSnackbarMessage={setSnackbarMessage}
                adminData={selectedRow}
            />

            <Dialog
                open={passwordDialogOpen}
                onClose={() => setPasswordDialogOpen(false)}
                maxWidth="xs"
                fullWidth
            >

                <DialogTitle sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between">
                        <Box>
                            <Typography variant="h6">
                                {en_ja["Change Admin Password"]}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={() => setPasswordDialogOpen(false)}
                            sx={{
                                mt: "-4px",
                                borderRadius: "50%",
                                width: 36,
                                height: 36,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                transition: "background-color 0.2s ease",

                                "&:hover": {
                                    backgroundColor: "rgba(0,0,0,0.08)",
                                },
                            }}
                        >
                            <CloseIcon />
                        </IconButton>

                    </Box>
                </DialogTitle>

                <DialogContent>

                    <TextField
                        fullWidth
                        margin="normal"
                        name="newPassword"
                        label={en_ja["Password"]}
                        type={showPassword ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        error={!!passwordErrors.newPassword}
                        helperText={passwordErrors.newPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />

                    <TextField
                        fullWidth
                        margin="normal"
                        name="confirmPassword"
                        label={en_ja["Confirm Password"]}
                        type={showConfirmPassword ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        error={!!passwordErrors.confirmPassword}
                        helperText={passwordErrors.confirmPassword}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() =>
                                            setShowConfirmPassword(!showConfirmPassword)
                                        }
                                        edge="end"
                                        size="small"
                                    >
                                        {showConfirmPassword ? (
                                            <VisibilityOff fontSize="small" />
                                        ) : (
                                            <Visibility fontSize="small" />
                                        )}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setPasswordDialogOpen(false)} variant="outlined">
                        {en_ja["Cancel"]}
                    </Button>

                    <LoadingButton
                        onClick={handleSetPassword}
                        loading={loading}
                        variant="contained"
                        sx={{ backgroundColor: "rgba(220, 53, 69, 1)" }}
                    >
                        {en_ja["Confirm"]}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default AdminManagement;